/**
 * Framework-free CodeMirror 6 kernel.
 *
 * Language, theme, read-only, the change gutter, and optional language
 * intelligence live in Compartments so a reconfigure keeps the document, undo
 * history, cursor, and scroll — the thing a remount throws away. Hosts
 * (desktop, web, tests) mount into an HTMLElement; this package does not
 * import React, Electron, or the store.
 */
import { indentWithTab } from "@codemirror/commands";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { oneDark } from "@codemirror/theme-one-dark";
import { basicSetup } from "codemirror";
import { ghostTextExtension, type InlineCompletionProvider } from "./ghostText.js";
import { changeGutter } from "./gutter.js";
import {
  intelligenceExtension,
  type IntelligenceHooks,
  type LanguageIntelligence,
} from "./intelligence.js";
import { languageFor, type EditorLanguageId } from "./language.js";

export type EditorAppearance = "light" | "dark";

export interface EditorSelection {
  path: string;
  from: number;
  to: number;
  text: string;
  startLine: number;
  endLine: number;
}

export interface EditorMountOptions {
  path?: string;
  document?: string;
  appearance?: EditorAppearance;
  readOnly?: boolean;
  changedLines?: ReadonlySet<number>;
  intelligence?: LanguageIntelligence;
  /** Model-backed ghost text (Tab to accept). Absent = no suggestions, no calls. */
  inlineCompletion?: InlineCompletionProvider;
  onChange?: (next: string) => void;
  onSave?: () => void;
  onSelection?: (selection: EditorSelection) => void;
  onDefinition?: IntelligenceHooks["onDefinition"];
  onDefinitionError?: IntelligenceHooks["onDefinitionError"];
}

export interface EditorKernel {
  readonly view: EditorView;
  readonly path: string;
  readonly languageId: EditorLanguageId | null;
  setPath(path: string): void;
  setAppearance(appearance: EditorAppearance): void;
  setChangedLines(lines?: ReadonlySet<number>): void;
  setReadOnly(readOnly: boolean): void;
  setIntelligence(intelligence?: LanguageIntelligence): void;
  setInlineCompletion(provider?: InlineCompletionProvider): void;
  setDocument(text: string): void;
  revealLine(line: number): void;
  focus(): void;
  destroy(): void;
  getSelection(): EditorSelection;
}

const BASE_THEME = EditorView.theme({
  "&": { height: "100%", fontSize: "12px" },
  ".cm-scroller": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
});

function themeExt(appearance: EditorAppearance): Extension {
  return appearance === "dark" ? oneDark : [];
}

function readOnlyExt(readOnly: boolean): Extension {
  return [EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)];
}

export function selectionFrom(path: string, state: EditorState): EditorSelection {
  const range = state.selection.main;
  const from = range.from;
  const to = range.to;
  const endPos = to > from ? to - 1 : to;
  return {
    path,
    from,
    to,
    text: state.sliceDoc(from, to),
    startLine: state.doc.lineAt(from).number,
    endLine: state.doc.lineAt(endPos).number,
  };
}

export function mountEditor(parent: HTMLElement, options: EditorMountOptions = {}): EditorKernel {
  let path = options.path ?? "";
  let languageId: EditorLanguageId | null = languageFor(path).languageId;
  let applyingExternal = false;

  const languageConf = new Compartment();
  const themeConf = new Compartment();
  const readOnlyConf = new Compartment();
  const changeGutterConf = new Compartment();
  const intelligenceConf = new Compartment();
  const ghostConf = new Compartment();
  let intelligence = options.intelligence;
  let inlineCompletion = options.inlineCompletion;
  let intelligenceGeneration = 0;

  const hooks: IntelligenceHooks & {
    onChange?: (next: string) => void;
    onSave?: () => void;
    onSelection?: (selection: EditorSelection) => void;
  } = {
    ...(options.onChange ? { onChange: options.onChange } : {}),
    ...(options.onSave ? { onSave: options.onSave } : {}),
    ...(options.onSelection ? { onSelection: options.onSelection } : {}),
    ...(options.onDefinition ? { onDefinition: options.onDefinition } : {}),
    ...(options.onDefinitionError ? { onDefinitionError: options.onDefinitionError } : {}),
  };

  const pathOf = (): string => path;
  const generationOf = (): number => intelligenceGeneration;
  const intelExt = (next?: LanguageIntelligence): Extension =>
    intelligenceExtension(next, pathOf, generationOf, hooks);

  const mapped = languageFor(path);
  const state = EditorState.create({
    doc: options.document ?? "",
    extensions: [
      basicSetup,
      keymap.of([
        {
          key: "Mod-s",
          preventDefault: true,
          run: () => {
            hooks.onSave?.();
            return true;
          },
        },
        indentWithTab,
      ]),
      languageConf.of(mapped.extension),
      themeConf.of(themeExt(options.appearance ?? "light")),
      readOnlyConf.of(readOnlyExt(options.readOnly === true)),
      changeGutterConf.of(changeGutter(options.changedLines)),
      intelligenceConf.of(intelExt(intelligence)),
      ghostConf.of(ghostTextExtension(inlineCompletion, pathOf)),
      BASE_THEME,
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          // Invalidate in-flight hover/definition/completion for this path.
          // Do not reconfigure the intelligence Compartment — generation is
          // a counter the adapter reads after await.
          intelligenceGeneration += 1;
          if (!applyingExternal) {
            hooks.onChange?.(update.state.doc.toString());
          }
        }
        if (update.selectionSet) {
          hooks.onSelection?.(selectionFrom(path, update.state));
        }
      }),
    ],
  });

  const view = new EditorView({ state, parent });

  function reconfigure(compartment: Compartment, extension: Extension): void {
    view.dispatch({ effects: compartment.reconfigure(extension) });
  }

  const kernel: EditorKernel = {
    get view(): EditorView {
      return view;
    },
    get path(): string {
      return path;
    },
    get languageId(): EditorLanguageId | null {
      return languageId;
    },
    setPath(nextPath: string): void {
      path = nextPath;
      intelligenceGeneration += 1;
      const next = languageFor(nextPath);
      languageId = next.languageId;
      reconfigure(languageConf, next.extension);
    },
    setAppearance(appearance: EditorAppearance): void {
      reconfigure(themeConf, themeExt(appearance));
    },
    setChangedLines(lines?: ReadonlySet<number>): void {
      reconfigure(changeGutterConf, changeGutter(lines));
    },
    setReadOnly(readOnly: boolean): void {
      reconfigure(readOnlyConf, readOnlyExt(readOnly));
    },
    setIntelligence(next?: LanguageIntelligence): void {
      if (next === intelligence) return;
      intelligence = next;
      intelligenceGeneration += 1;
      reconfigure(intelligenceConf, intelExt(next));
    },
    setInlineCompletion(next?: InlineCompletionProvider): void {
      if (next === inlineCompletion) return;
      inlineCompletion = next;
      reconfigure(ghostConf, ghostTextExtension(next, pathOf));
    },
    setDocument(text: string): void {
      const current = view.state.doc.toString();
      if (current === text) return;
      const sel = view.state.selection.main;
      const max = text.length;
      applyingExternal = true;
      try {
        // Generation bumps in the docChanged listener during this dispatch,
        // so an in-flight hover/definition started against the previous
        // document is discarded without reconfiguring intelligence.
        view.dispatch({
          changes: { from: 0, to: current.length, insert: text },
          selection: { anchor: Math.min(sel.anchor, max), head: Math.min(sel.head, max) },
        });
      } finally {
        applyingExternal = false;
      }
    },
    revealLine(line: number): void {
      const lines = view.state.doc.lines;
      const n = Math.min(Math.max(1, line), lines);
      const pos = view.state.doc.line(n).from;
      view.dispatch({
        selection: { anchor: pos },
        effects: EditorView.scrollIntoView(pos, { y: "center" }),
      });
    },
    focus(): void {
      view.focus();
    },
    destroy(): void {
      intelligenceGeneration += 1;
      view.destroy();
    },
    getSelection(): EditorSelection {
      return selectionFrom(path, view.state);
    },
  };

  return kernel;
}
